import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useConfirm } from "@/shared/contexts/confirm-context";
import { usePaymentMethods } from "@/features/paymentMethods/hooks/usePaymentMethods";
import { formatPaymentMethodLabel, isMpesaType } from "@/features/paymentMethods/services/paymentMethodsApi";

interface RecordPaymentValues {
  amountPaid: number;
  paymentDate: string;
  paymentMethodAccountId: string;
  paymentMethod?: string;
  paymentReference?: string;
  notes?: string;
}

interface RecordPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceNumber?: string;
  maxAmount?: number;
  isSubmitting?: boolean;
  onSubmit: (values: RecordPaymentValues) => void;
}

export const RecordPaymentDialog = ({
  open,
  onOpenChange,
  invoiceNumber,
  maxAmount,
  isSubmitting = false,
  onSubmit,
}: RecordPaymentDialogProps) => {
  const [amountPaid, setAmountPaid] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentMethodAccountId, setPaymentMethodAccountId] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [notes, setNotes] = useState("");
  const confirm = useConfirm();
  const paymentMethodsQuery = usePaymentMethods();
  const paymentMethods = paymentMethodsQuery.data ?? [];
  const selectedPaymentMethod = paymentMethods.find((m) => m.id === paymentMethodAccountId);
  const requiresReference = selectedPaymentMethod ? isMpesaType(selectedPaymentMethod.type) : false;

  useEffect(() => {
    if (!open) {
      return;
    }

    setAmountPaid(maxAmount ? String(maxAmount) : "");
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setPaymentMethodAccountId("");
    setPaymentReference("");
    setNotes("");
  }, [open, maxAmount]);

  const handleSubmit = () => {
    if (!amountPaid || !paymentDate || !paymentMethodAccountId) {
      return;
    }
    if (requiresReference && !paymentReference.trim()) {
      return;
    }

    confirm({
      title: "Record payment?",
      description: `Record settlement for ${invoiceNumber ?? "the selected invoice"}.`,
      confirmLabel: "Record payment",
      body: (
        <div className="space-y-1">
          <p className="flex justify-between">
            <span className="text-muted-foreground">Amount</span>
            <span className="font-medium">KES {Number(amountPaid).toLocaleString()}</span>
          </p>
          <p className="flex justify-between">
            <span className="text-muted-foreground">Method</span>
            <span className="font-medium">{selectedPaymentMethod ? formatPaymentMethodLabel(selectedPaymentMethod) : "-"}</span>
          </p>
        </div>
      ),
      onConfirm: () => {
        onSubmit({
          amountPaid: Number(amountPaid),
          paymentDate: new Date(paymentDate).toISOString(),
          paymentMethodAccountId,
          paymentMethod: selectedPaymentMethod?.name,
          paymentReference: paymentReference.trim() || undefined,
          notes: notes.trim() || undefined,
        });
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>
            Record settlement details for {invoiceNumber ?? "the selected invoice"}.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="space-y-2">
            <Label htmlFor="payment-amount">Amount Paid</Label>
            <Input
              id="payment-amount"
              type="number"
              min={0}
              value={amountPaid}
              onChange={(event) => setAmountPaid(event.target.value)}
              placeholder="Enter paid amount"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment-date">Payment Date</Label>
            <DatePicker
              id="payment-date"
              value={paymentDate}
              onChange={setPaymentDate}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment-method">Payment Method</Label>
            <Select value={paymentMethodAccountId} onValueChange={setPaymentMethodAccountId}>
              <SelectTrigger id="payment-method">
                <SelectValue placeholder="Select payment method" />
              </SelectTrigger>
              <SelectContent>
                {paymentMethods.map((method) => (
                  <SelectItem key={method.id} value={method.id}>
                    {formatPaymentMethodLabel(method)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment-reference">Reference</Label>
            <Input
              id="payment-reference"
              value={paymentReference}
              onChange={(event) => setPaymentReference(event.target.value)}
              placeholder={requiresReference ? "Required for M-Pesa payments" : "Optional transaction reference"}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment-notes">Notes</Label>
            <Textarea
              id="payment-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Optional payment notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !amountPaid || !paymentDate || !paymentMethodAccountId}>
            {isSubmitting ? "Saving..." : "Record Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
